'use client';

import NumberFormatter from '@/utils/bn';
import { TimeZoneUtils } from '@/utils/timezone-utils';
import { TZDate } from '@date-fns/tz';
import {
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
} from 'date-fns';
import { useTranslations } from 'next-intl';
import { useParams } from 'next/navigation';

type BatchStartDayLeftProps = {
  batchStartDate: string;
  courseCardView?: boolean;
};

type RemainTime =
  | {
      value: number;
      unit: string;
    }
  | undefined;

export default function BatchStartCountdown({
  batchStartDate,
  courseCardView,
}: BatchStartDayLeftProps) {
  const userTimezone = TimeZoneUtils.detectUserTimezone();
  const now = new TZDate(new Date(), userTimezone);
  const { locale } = useParams();
  const t = useTranslations();

  const normalizedDate = batchStartDate.replace('T', ' ').replace('Z', '');

  const startDate =
    TimeZoneUtils.detectUserTimezoneAndDisplayDate(
      normalizedDate,
      'eee MMM dd yyyy HH:mm:ss',
    ) || '';

  const timeDifferences = [
    {
      value: differenceInDays(startDate, now),
      unit: courseCardView ? 'common.days-left' : 'common.days',
    },
    {
      value: differenceInHours(startDate, now),
      unit: courseCardView ? 'common.hour-left' : 'common.hours',
    },
    {
      value: differenceInMinutes(startDate, now),
      unit: courseCardView ? 'common.minute-left' : 'common.minutes',
    },
  ];

  const remainingTime = timeDifferences.find(({ value }) => value > 0);

  const admissionTimeText = (remainingTime: RemainTime) => {
    if (!remainingTime) {
      return courseCardView
        ? t('common.no-time-left')
        : t('course-details.courseBenefitsCard.admission-time-over');
    }

    const formattedValue =
      locale === 'bn'
        ? NumberFormatter(remainingTime.value)
        : remainingTime.value;

    return `${formattedValue} ${t(remainingTime.unit)}`;
  };

  return (
    <p
      className={`${courseCardView ? 'text-sm text-gray-700' : 'text-lg font-semibold text-gray-900'} `}
    >
      {admissionTimeText(remainingTime)}
    </p>
  );
}
